import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

interface SettingNumberProps {
  apiBaseUrl: string;
  settingKey: string;
  title: string;
  description?: string;
  defaultValue: number;
  min?: number;
  saveDescription?: string;
  suffix?: React.ReactNode;
  onSaved?: (value: number) => void;
}

const parseIntValue = (raw: unknown, fallback: number, min = 0) => {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < min) return fallback;
  return parsed;
};

const SettingNumber: React.FC<SettingNumberProps> = ({
  apiBaseUrl,
  settingKey,
  title,
  description,
  defaultValue,
  min = 0,
  saveDescription,
  suffix,
  onSaved,
}) => {
  const [value, setValue] = useState<string>(String(defaultValue));
  const [savedValue, setSavedValue] = useState<number>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchSetting = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/settings/${settingKey}`, {
          withCredentials: true,
        });
        if (active) {
          const next = parseIntValue(response.data?.value, defaultValue, min);
          setValue(String(next));
          setSavedValue(next);
          setError(null);
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          if (active) {
            setValue(String(defaultValue));
            setSavedValue(defaultValue);
          }
        } else if (active) {
          console.error(`Error fetching setting ${settingKey}:`, err);
          setError('Failed to load setting');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchSetting();
    return () => {
      active = false;
    };
  }, [apiBaseUrl, settingKey, defaultValue, min]);

  const persist = async () => {
    const next = parseIntValue(value, savedValue, min);
    if (next === savedValue) {
      setValue(String(savedValue));
      return;
    }
    setUpdating(true);
    setSaved(false);
    try {
      const response = await axios.put(
        `${apiBaseUrl}/settings/${settingKey}`,
        { value: next, description: saveDescription || title },
        { withCredentials: true }
      );
      const stored = parseIntValue(response.data?.value, next, min);
      setValue(String(stored));
      setSavedValue(stored);
      setError(null);
      setSaved(true);
      onSaved?.(stored);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      console.error(`Error updating setting ${settingKey}:`, err);
      setError(err.response?.data?.message || 'Failed to update setting');
      setValue(String(savedValue));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div>
      <label
        className="form-check-label"
        htmlFor={`setting-${settingKey}`}
        style={{ fontSize: '1.1rem', fontWeight: 500 }}
      >
        {title}
      </label>
      {description && <div className="text-muted small mt-1">{description}</div>}
      <div className="d-flex align-items-center gap-2 mt-2">
        <input
          id={`setting-${settingKey}`}
          type="number"
          min={min}
          step={1}
          className="form-control"
          style={{ maxWidth: '10rem' }}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={persist}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              (event.target as HTMLInputElement).blur();
            }
          }}
          disabled={loading || updating}
        />
        {suffix}
        {(loading || updating) && <FontAwesomeIcon icon={faSync} spin className="text-muted" />}
        {saved && !updating && <span className="text-success small">Saved</span>}
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default SettingNumber;
